import React from "react";
import {
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  IconButton,
  Box,
  FormControl,
  Select,
  MenuItem,
  Chip,
  InputAdornment,
  Snackbar,
  Alert,
  Divider,
  Fade,
  Popover,
  ListSubheader,
  ListItemText,
  ListItemButton,
  ListItemIcon as MuiListItemIcon,
  Checkbox,
} from "@mui/material";
import classnames from "classnames";
import forage from "localforage";
import { isMobile } from "react-device-detect";
import { useExchangeAdapter, useExchangeSwitch, useAvailableExchanges } from "../../../contexts/ExchangeContext";
import { getExchangeAdapter } from "../../../exchanges";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import ExportIcon from "@mui/icons-material/ContentCopy";
import ImportIcon from "@mui/icons-material/UploadFile";
import CloseIconSmall from "@mui/icons-material/Close";
import RefreshIntervalSelector from "../../RefreshIntervalSelector";
import { MerchantFollowProvider } from "../../../contexts/MerchantFollowContext";

interface LayoutProps {
  backButton?: any;
  className?: string;
  title?: string;
  children?: React.ReactNode;
}

export interface MerchantFollow {
  nickname: string;
  colorIndex: number;
}

export const HIGHLIGHT_COLORS = [
  { bg: "!bg-red-100", text: "!text-red-600", name: "Đỏ", chipColor: "#fee2e2" },
  { bg: "!bg-blue-100", text: "!text-blue-600", name: "Xanh dương", chipColor: "#dbeafe" },
  { bg: "!bg-green-100", text: "!text-green-600", name: "Xanh lá", chipColor: "#dcfce7" },
  { bg: "!bg-yellow-100", text: "!text-yellow-600", name: "Vàng", chipColor: "#fef9c3" },
  { bg: "!bg-purple-100", text: "!text-purple-600", name: "Tím", chipColor: "#f3e8ff" },
  { bg: "!bg-pink-100", text: "!text-pink-600", name: "Hồng", chipColor: "#fce7f3" },
  { bg: "!bg-orange-100", text: "!text-orange-600", name: "Cam", chipColor: "#ffedd5" },
  { bg: "!bg-teal-100", text: "!text-teal-600", name: "Xanh ngọc", chipColor: "#ccfbf1" },
  { bg: "!bg-amber-100", text: "!text-amber-600", name: "Nâu", chipColor: "#fef3c7" },
  { bg: "!bg-gray-100", text: "!text-gray-600", name: "Xám", chipColor: "#f3f4f6" },
  { bg: "!bg-rose-100", text: "!text-rose-600", name: "Đỏ đậm", chipColor: "#ffe4e6" },
  { bg: "!bg-indigo-100", text: "!text-indigo-600", name: "Xanh coban", chipColor: "#e0e7ff" },
  { bg: "!bg-emerald-100", text: "!text-emerald-600", name: "Xanh mint", chipColor: "#d1fae5" },
  { bg: "!bg-fuchsia-100", text: "!text-fuchsia-600", name: "Tía nhạt", chipColor: "#fae8ff" },
  { bg: "!bg-lime-100", text: "!text-lime-600", name: "Vàng nhạt", chipColor: "#ecfccb" },
  { bg: "!bg-sky-100", text: "!text-sky-600", name: "Xanh sky", chipColor: "#e0f2fe" },
  { bg: "!bg-orange-200", text: "!text-orange-700", name: "Đỏ cam", chipColor: "#fed7aa" },
  { bg: "!bg-slate-100", text: "!text-slate-600", name: "Xám xanh", chipColor: "#f1f5f9" },
  { bg: "!bg-violet-100", text: "!text-violet-600", name: "Tím than", chipColor: "#ede9fe" },
  { bg: "!bg-rose-200", text: "!text-rose-700", name: "Hồng đậm", chipColor: "#fecdd3" },
] as const;

export const MAX_MERCHANTS = 20;

// Emoji icons for exchange selector
const exchangeIcons: Record<string, string> = {
  binance: "🔶",
  okx: "🟣",
  mexc: "🟢",
};

// Migration function to convert old format to new format
const migrateMerchantData = async () => {
  const oldData = await forage.getItem("followingMerchant");
  const newData = await forage.getItem("followingMerchants");

  if (oldData && typeof oldData === "string" && !newData) {
    const merchants: MerchantFollow[] = [{ nickname: oldData, colorIndex: 0 }];
    await forage.setItem("followingMerchants", merchants);
    await forage.removeItem("followingMerchant");
    return merchants;
  }

  // Fix colorIndex out of range (old data with colorIndex >= 20)
  if (newData && Array.isArray(newData)) {
    const fixed = newData.map((m) =>
      m.colorIndex >= HIGHLIGHT_COLORS.length ? { ...m, colorIndex: 0 } : m
    );
    if (fixed !== newData) {
      await forage.setItem("followingMerchants", fixed);
    }
    return fixed as MerchantFollow[] | null;
  }

  return newData as MerchantFollow[] | null;
};

// Helper: check duplicate (case-insensitive)
const isDuplicate = (nickname: string, existing: MerchantFollow[]): boolean =>
  existing.some((m) => m.nickname.toLowerCase() === nickname.toLowerCase());

// Helper: parse nicknames from text (comma or newline separated)
const parseNicknames = (text: string): string[] =>
  text.split(/[\n,]+/).map((n) => n.trim()).filter(Boolean);

// Helper: prepare import preview with sequential colors
const prepareImportPreview = (
  nicknames: string[],
  currentMerchants: MerchantFollow[]
): MerchantFollow[] => {
  const existingNames = new Set(currentMerchants.map((m) => m.nickname.toLowerCase()));
  let colorIndex = currentMerchants.length % HIGHLIGHT_COLORS.length;
  return nicknames
    .filter((n) => !existingNames.has(n.toLowerCase()))
    .map((nickname) => ({
      nickname,
      colorIndex: colorIndex++ % HIGHLIGHT_COLORS.length,
    }));
};

// ── Module-level sx objects (avoids TSX parser issues with & CSS selectors) ──
const SX_DIALOG: Record<string, unknown> = {
  "& .MuiDialog-paper": { bgcolor: "#0f172a", backgroundImage: "none" },
};
const SX_INPUT_ROOT: Record<string, unknown> = {
  "& .MuiOutlinedInput-root": {
    bgcolor: "#1e293b",
    "& fieldset": { borderColor: "#334155" },
    "&:hover fieldset": { borderColor: "#3b82f6" },
    "&.Mui-focused fieldset": { borderColor: "#3b82f6" },
  },
};
const SX_IMPORT_INPUT: Record<string, unknown> = {
  "& .MuiOutlinedInput-input": { bgcolor: "#1e293b", color: "text.primary" },
  "& fieldset": { borderColor: "#334155" },
  "&:hover fieldset": { borderColor: "#3b82f6" },
  "&.Mui-focused fieldset": { borderColor: "#3b82f6" },
};
const SX_IMPORT_FIELD: Record<string, unknown> = {
  "& .MuiOutlinedInput-root": { bgcolor: "#1e293b" },
};
const SX_SELECT: Record<string, unknown> = {
  "& .MuiOutlinedInput-notchedOutline": { borderColor: "#334155" },
  "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#3b82f6" },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#3b82f6" },
  bgcolor: "#0f172a",
  fontSize: "0.75rem",
};
const BTN_PRIMARY: Record<string, unknown> = {
  bgcolor: "#3b82f6",
  "&:hover": { bgcolor: "#2563eb" },
  fontSize: "0.8rem",
};
const BTN_OUTLINE: Record<string, unknown> = {
  borderColor: "#3b82f6",
  color: "#3b82f6",
  fontSize: "0.8rem",
  "&:hover": { borderColor: "#2563eb", bgcolor: "rgba(59,130,246,0.08)" },
};
const BTN_OUTLINE_CLOSE: Record<string, unknown> = {
  borderColor: "#334155",
  color: "#94a3b8",
  "&:hover": { borderColor: "#475569", bgcolor: "rgba(148,163,184,0.05)" },
};
const BTN_IMPORT: Record<string, unknown> = {
  bgcolor: "#3b82f6",
  "&:hover": { bgcolor: "#2563eb" },
};
const ICON_DELETE: Record<string, unknown> = {
  color: "#64748b",
  "&:hover": { color: "#f87171", bgcolor: "rgba(248,113,113,0.1)" },
};
const ALERT_WARNING: Record<string, unknown> = {
  bgcolor: "#7c2d12",
  color: "#fbbf24",
  "& .MuiAlert-icon": { color: "#fbbf24" },
};
const ALERT_SUCCESS: Record<string, unknown> = {
  bgcolor: "#14532d",
  color: "#86efac",
  "& .MuiAlert-icon": { color: "#86efac" },
};

function LayoutPrice(props: LayoutProps) {
  const adapter = useExchangeAdapter();
  const switchExchange = useExchangeSwitch();
  const availableExchanges = useAvailableExchanges();
  const accessLevel = window.location.href.includes("admin") ? 2 : window.location.href.includes("premium") ? 1 : 0;
  const [menuAnchor, setMenuAnchor] = React.useState<null | HTMLElement>(null);
  const [merchants, setMerchants] = React.useState<MerchantFollow[]>([]);
  const [dialogOpen, setDialogOpen] = React.useState(false);
  const [newMerchantName, setNewMerchantName] = React.useState("");
  const [searchQuery, setSearchQuery] = React.useState("");
  const [duplicateWarning, setDuplicateWarning] = React.useState<string | null>(null);

  // Bulk import
  const [importModalOpen, setImportModalOpen] = React.useState(false);
  const [importText, setImportText] = React.useState("");
  const [importPreview, setImportPreview] = React.useState<MerchantFollow[]>([]);

  // Undo after immediate delete
  const [removedMerchant, setRemovedMerchant] = React.useState<MerchantFollow | null>(null);
  const [undoOpen, setUndoOpen] = React.useState(false);

  // Toast
  const [toastOpen, setToastOpen] = React.useState(false);

  // Lazy-load persisted data
  React.useEffect(() => {
    (async () => {
      const merchantsData = await migrateMerchantData();
      if (merchantsData && Array.isArray(merchantsData)) {
        setMerchants(merchantsData);
      }
    })();
  }, []);

  const handleOpenDialog = () => {
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setNewMerchantName("");
    setSearchQuery("");
    setDuplicateWarning(null);
  };

  const handleAddMerchant = async () => {
    const trimmed = newMerchantName.trim();
    if (!trimmed) return;

    if (isDuplicate(trimmed, merchants)) {
      setDuplicateWarning(`Thương gia "${trimmed}" đã có trong danh sách.`);
      return;
    }

    setDuplicateWarning(null);

    if (merchants.length >= MAX_MERCHANTS) {
      setDuplicateWarning(`Danh sách đã đủ ${MAX_MERCHANTS} thương gia.`);
      return;
    }

    const nextColorIndex = merchants.length % HIGHLIGHT_COLORS.length;
    const newMerchant: MerchantFollow = {
      nickname: trimmed,
      colorIndex: nextColorIndex,
    };
    const updatedMerchants = [...merchants, newMerchant];
    setMerchants(updatedMerchants);
    await forage.setItem("followingMerchants", updatedMerchants);
    setNewMerchantName("");
  };

  const handleRemoveMerchant = async (index: number) => {
    const merchant = merchants[index];
    const updatedMerchants = merchants.filter((_, i) => i !== index);
    setMerchants(updatedMerchants);
    await forage.setItem("followingMerchants", updatedMerchants);
    setRemovedMerchant(merchant);
    setUndoOpen(true);
  };

  const handleUndoRemoval = () => {
    if (!removedMerchant) return;
    const restored = [...merchants, removedMerchant];
    setMerchants(restored);
    forage.setItem("followingMerchants", restored).catch(() => {});
    setRemovedMerchant(null);
    setUndoOpen(false);
  };

  const handleUpdateMerchantColor = async (
    index: number,
    colorIndex: number
  ) => {
    const updatedMerchants = merchants.map((merchant, i) =>
      i === index ? { ...merchant, colorIndex } : merchant
    );
    setMerchants(updatedMerchants);
    await forage.setItem("followingMerchants", updatedMerchants);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      handleAddMerchant();
    }
  };

  // Bulk import handlers
  const handleImportTextChange = (text: string) => {
    setImportText(text);
    const nicknames = parseNicknames(text);
    const preview = prepareImportPreview(nicknames, merchants);
    setImportPreview(preview);
  };

  const handleImportConfirm = async () => {
    if (importPreview.length === 0) return;
    const updatedMerchants = [...merchants, ...importPreview];
    setMerchants(updatedMerchants);
    await forage.setItem("followingMerchants", updatedMerchants);
    setImportText("");
    setImportPreview([]);
    setImportModalOpen(false);
  };

  const handleExport = async () => {
    if (merchants.length === 0) return;
    const text = merchants.map((m) => m.nickname).join("\n");
    await navigator.clipboard.writeText(text);
    setToastOpen(true);
  };

  // Filtered merchants
  const filteredMerchants = React.useMemo(() => {
    if (!searchQuery.trim()) return merchants;
    const q = searchQuery.toLowerCase();
    return merchants.filter((m) => m.nickname.toLowerCase().includes(q));
  }, [merchants, searchQuery]);

  const isPremium = accessLevel >= 1;

  return (
    <div className={props.className}>
      <div
        className={classnames({
          "w-full text-center fixed top-0 z-50 flex justify-center": true,
          // Free: solid color backgrounds
          "bg-emerald-600": props.title === "Bảng USDT" && !isPremium,
          "bg-sky-500": props.title === "Bảng USDC" && !isPremium,
          "bg-cyan-800": props.title === "Bảng FDUSD" && !isPremium,
          "bg-amber-600": props.title === "Bảng BTC" && !isPremium,
          "bg-zinc-600": props.title === "Bảng ETH" && !isPremium,
          // Premium: dark slate with amber accents
          "bg-slate-900": isPremium,
          "border-b border-slate-800": isPremium,
        })}
        style={isPremium ? {
          boxShadow: "0 1px 3px rgba(0,0,0,0.3)",
        } : undefined}
      >
        {/* Premium static background — 0 animations, 0 CPU */}
        {isPremium && (
          <div className="absolute inset-0 pointer-events-none overflow-hidden">
            <div
              className="absolute inset-0"
              style={{
                background:
                  "radial-gradient(ellipse at 50% 0%, rgba(245,158,11,0.06) 0%, transparent 60%)",
              }}
            />
            <div
              className="absolute bottom-0 left-0 right-0 h-px"
              style={{
                background:
                  "linear-gradient(90deg, transparent 5%, rgba(245,158,11,0.4) 50%, transparent 95%)",
              }}
            />
          </div>
        )}
        {/* Content (always on top) */}
        <div className="relative z-10">
          <Typography
            variant="h6"
            className="py-1"
            sx={{
              color: isPremium ? "#f59e0b" : "text.primary",
              fontWeight: isPremium ? 700 : 400,
            }}
          >
            {isPremium && <span>👑</span>}
            {isMobile ? "📱" : ""} {props.title}{" "}
          {accessLevel >= 1 && (
            <>
              <Chip
                label={adapter.displayName}
                size="small"
                clickable
                onClick={(e) => setMenuAnchor(e.currentTarget)}
                sx={{
                  fontSize: "0.7rem",
                  fontWeight: 600,
                  cursor: "pointer",
                  bgcolor: "rgba(245,158,11,0.12)",
                  color: "#f59e0b",
                  border: "1px solid rgba(245,158,11,0.3)",
                  borderRadius: 1.5,
                  "&:hover": { bgcolor: "rgba(245,158,11,0.2)" },
                }}
              />
              <Popover
                open={Boolean(menuAnchor)}
                anchorEl={menuAnchor}
                onClose={() => setMenuAnchor(null)}
                anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
                transformOrigin={{ vertical: "top", horizontal: "center" }}
                PaperProps={{
                  sx: {
                    bgcolor: "#0f172a",
                    mt: 0.5,
                    borderRadius: 1.5,
                    border: "1px solid rgba(245,158,11,0.3)",
                    boxShadow: "0 8px 24px rgba(0,0,0,0.4)",
                    minWidth: 180,
                    p: 0.5,
                  },
                }}
              >
                {availableExchanges.map((ex) => {
                  const ad = getExchangeAdapter(ex);
                  const isActive = ex === adapter.id;
                  const icon = exchangeIcons[ex] || "🏦";
                  return (
                    <ListItemButton
                      key={ex}
                      onClick={() => {
                        switchExchange(ex);
                        setMenuAnchor(null);
                      }}
                      sx={{
                        px: 1.2,
                        py: 0.35,
                        borderRadius: 1.5,
                        bgcolor: isActive ? "rgba(245,158,11,0.12)" : "transparent",
                        border: isActive ? "1px solid rgba(245,158,11,0.3)" : "1px solid transparent",
                        "&:hover": { bgcolor: "rgba(245,158,11,0.08)", borderColor: "rgba(245,158,11,0.2)" },
                        minHeight: 30,
                      }}
                    >
                      <MuiListItemIcon sx={{ minWidth: 32, color: isActive ? "#f59e0b" : "#94a3b8", fontSize: "1rem" }}>
                        {icon}
                      </MuiListItemIcon>
                      <ListItemText
                        primary={ad.displayName}
                        primaryTypographyProps={{
                          fontSize: "0.85rem",
                          fontWeight: isActive ? 600 : 400,
                          color: isActive ? "#f59e0b" : "#e2e8f0",
                        }}
                      />
                      {isActive && (
                        <Checkbox
                          size="small"
                          checked
                          sx={{
                            color: "#f59e0b",
                            "&.Mui-checked": { color: "#f59e0b" },
                            ml: 0.5,
                          }}
                        />
                      )}
                    </ListItemButton>
                  );
                })}
              </Popover>
            </>
          )}
        </Typography>
        </div>
        <div className="absolute top-2 right-2 flex items-center gap-2">
          <RefreshIntervalSelector />
          <Typography
            variant="subtitle2"
            className={
              "bg-red-100 text-red-500 hover:cursor-pointer hover:bg-red-200 rounded-md px-2"
            }
            onClick={handleOpenDialog}
          >
            🕵️ {merchants.length > 0 ? `${merchants.length} thương gia` : "Tìm thương gia?"}
          </Typography>
        </div>
      </div>
      <div className="h-8"></div>
      <main className="relative"><MerchantFollowProvider value={merchants}>{props.children}</MerchantFollowProvider></main>

      <div className="h-24"></div>

      {/* Main Merchant Management Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
        TransitionComponent={Fade}
        TransitionProps={{ timeout: 300 }}
        sx={SX_DIALOG}
      >
        <DialogTitle sx={{ bgcolor: "#0f172a", pb: 1.5 }}>
          Quản lý thương gia theo dõi
        </DialogTitle>

        <DialogContent sx={{ pt: 1.5, px: 2, pb: 2 }}>
          {/* ── Actions Toolbar ── */}
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, alignItems: "center", mb: 1.5 }}>
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={() => {
                setDialogOpen(false);
                setDialogOpen(true);
                setTimeout(() => {
                  const input = document.querySelector<HTMLInputElement>('input[placeholder="Tên thương gia"]');
                  input?.focus();
                }, 100);
              }}
              size="small"
              sx={BTN_PRIMARY}
            >
              Thêm mới
            </Button>
            <Button
              variant="outlined"
              color="primary"
              startIcon={<ImportIcon />}
              onClick={() => setImportModalOpen(true)}
              size="small"
              sx={BTN_OUTLINE}
            >
              Nhập hàng loạt
            </Button>
            <Button
              variant="outlined"
              color="primary"
              startIcon={<ExportIcon />}
              onClick={handleExport}
              size="small"
              disabled={merchants.length === 0}
              sx={BTN_OUTLINE}
            >
              Xuất danh sách
            </Button>
            <Box sx={{ ml: "auto" }}>
              <Chip
                label={`${merchants.length}/${MAX_MERCHANTS}`}
                size="small"
                sx={{
                  bgcolor: merchants.length >= MAX_MERCHANTS ? "rgba(239,68,68,0.15)" : "rgba(59,130,246,0.12)",
                  color: merchants.length >= MAX_MERCHANTS ? "#f87171" : "#60a5fa",
                  fontWeight: 600,
                  fontSize: "0.75rem",
                }}
              />
            </Box>
          </Box>

          {/* ── Add Section ── */}
          <Box sx={{ mb: 1.5 }}>
            <TextField
              fullWidth
              size="small"
              label="Tên thương gia"
              placeholder="Ví dụ: Dân Chơi Coin, BendTheTrend x HỏaTốc247..."
              value={newMerchantName}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                setNewMerchantName(e.target.value);
                setDuplicateWarning(null);
              }}
              onKeyDown={handleKeyDown}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={handleAddMerchant} edge="end" disabled={!newMerchantName.trim()}>
                      <AddIcon />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{
                mb: duplicateWarning ? 0.75 : 0,
                ...SX_INPUT_ROOT,
              }}
            />
            {duplicateWarning && (
              <Chip
                label={duplicateWarning}
                size="small"
                color="warning"
                sx={{ mt: 0.75, fontSize: "0.75rem" }}
              />
            )}
          </Box>

          <Divider sx={{ borderColor: "#1e293b", my: 1.5 }} />

          {/* ── Search Bar ── */}
          <Box sx={{ mb: 1.5 }}>
            <TextField
              fullWidth
              size="small"
              placeholder="Tìm trong danh sách..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: "#64748b", fontSize: 18 }} />
                  </InputAdornment>
                ),
                endAdornment: searchQuery ? (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setSearchQuery("")} size="small" sx={{ color: "#64748b" }}>
                      <CloseIconSmall sx={{ fontSize: 18 }} />
                    </IconButton>
                  </InputAdornment>
                ) : undefined,
              }}
              sx={{ ...SX_INPUT_ROOT }}
            />
            {searchQuery && (
              <Typography variant="caption" sx={{ color: "#64748b", mt: 0.5, display: "block" }}>
                {filteredMerchants.length} kết quả cho "{searchQuery}"
              </Typography>
            )}
          </Box>

          {/* ── Merchant List ── */}
          <Typography variant="subtitle2" sx={{ mb: 1, color: "text.secondary", fontSize: "0.8rem" }}>
            Danh sách thương gia:
          </Typography>
          {filteredMerchants.length === 0 ? (
            <Typography variant="body2" sx={{ color: "#64748b", textAlign: "center", py: 2 }}>
              {searchQuery ? "Không tìm thấy thương gia nào." : "Chưa có thương gia nào. Thêm thương gia để theo dõi!"}
            </Typography>
          ) : (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
              {filteredMerchants.map((merchant, idx) => {
                const actualIndex = merchants.indexOf(merchant);
                const color = HIGHLIGHT_COLORS[merchant.colorIndex];
                return (
                  <Box
                    key={actualIndex}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      p: 1,
                      borderRadius: 1.5,
                      border: "1px solid",
                      borderColor: "#1e293b",
                      bgcolor: "#1e293b",
                    }}
                  >
                    {/* Color dot */}
                    <Box
                      sx={{
                        width: 14,
                        height: 14,
                        borderRadius: "50%",
                        bgcolor: color.chipColor,
                        border: "1px solid",
                        borderColor: "#334155",
                        flexShrink: 0,
                      }}
                    />
                    {/* Nickname */}
                    <Typography
                      variant="body2"
                      sx={{ color: "text.primary", fontWeight: 500, flex: 1, minWidth: 0 }}
                      noWrap
                      title={merchant.nickname}
                    >
                      {merchant.nickname}
                    </Typography>
                    {/* Color select */}
                    <FormControl size="small">
                      <Select
                        value={merchant.colorIndex}
                        onChange={(event) =>
                          handleUpdateMerchantColor(actualIndex, Number(event.target.value))
                        }
                        displayEmpty
                        sx={SX_SELECT}
                      >
                        {HIGHLIGHT_COLORS.map((c, ci) => (
                          <MenuItem key={c.name} value={ci} sx={{ fontSize: "0.8rem" }}>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                              <Box
                                sx={{
                                  width: 10,
                                  height: 10,
                                  borderRadius: "50%",
                                  bgcolor: c.chipColor,
                                  border: "1px solid",
                                  borderColor: "#334155",
                                }}
                              />
                              <Typography variant="body2" sx={{ color: "text.primary" }}>
                                {c.name}
                              </Typography>
                            </Box>
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    {/* Delete */}
                    <IconButton
                      size="small"
                      onClick={() => handleRemoveMerchant(actualIndex)}
                      sx={ICON_DELETE}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                );
              })}
            </Box>
          )}

          <Box sx={{ mt: 2 }}>
            <Button
              variant="outlined"
              onClick={handleCloseDialog}
              fullWidth
              sx={BTN_OUTLINE_CLOSE}
            >
              Đóng
            </Button>
          </Box>
        </DialogContent>
      </Dialog>

      {/* ── Bulk Import Dialog ── */}
      <Dialog
        open={importModalOpen}
        onClose={() => {
          setImportModalOpen(false);
          setImportText("");
          setImportPreview([]);
        }}
        maxWidth="sm"
        fullWidth
        TransitionComponent={Fade}
        TransitionProps={{ timeout: 300 }}
        sx={SX_DIALOG}
      >
        <DialogTitle sx={{ bgcolor: "#0f172a" }}>
          Nhập hàng loạt thương gia
        </DialogTitle>

        <DialogContent sx={{ pt: 1.5, px: 2, pb: 2, bgcolor: "#0f172a" }}>
          <Typography variant="body2" sx={{ color: "#94a3b8", mb: 1.5, fontSize: "0.85rem" }}>
            Dán danh sách tên thương gia (mỗi dòng 1 tên, hoặc cách nhau bởi dấu phẩy):
          </Typography>
          <TextField
            multiline
            minRows={6}
            fullWidth
            size="small"
            placeholder="Ví dụ: Dân Chơi Coin | BendTheTrend, HỏaTốc247 | Merchant VIP"
            value={importText}
            onChange={(e) => handleImportTextChange(e.target.value)}
            InputProps={{
              sx: SX_IMPORT_INPUT,
            }}
            sx={SX_IMPORT_FIELD}
          />

          {importPreview.length > 0 && (
            <>
              <Divider sx={{ borderColor: "#1e293b", my: 1.5 }} />
              <Typography variant="subtitle2" sx={{ color: "#94a3b8", mb: 1, fontSize: "0.85rem" }}>
                Thêm dự kiến ({importPreview.length}):
              </Typography>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, maxHeight: 180, overflow: "auto" }}>
                {importPreview.map((m, i) => {
                  const color = HIGHLIGHT_COLORS[m.colorIndex];
                  return (
                    <Box key={i} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Box
                        sx={{
                          width: 10,
                          height: 10,
                          borderRadius: "50%",
                          bgcolor: color.chipColor,
                          border: "1px solid",
                          borderColor: "#334155",
                          flexShrink: 0,
                        }}
                      />
                      <Typography variant="body2" sx={{ color: "text.primary", fontSize: "0.85rem" }}>
                        {m.nickname}
                      </Typography>
                    </Box>
                  );
                })}
              </Box>
            </>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 2, pb: 2, bgcolor: "#0f172a" }}>
          <Button
            onClick={() => {
              setImportModalOpen(false);
              setImportText("");
              setImportPreview([]);
            }}
            sx={{ color: "#94a3b8" }}
          >
            Hủy
          </Button>
          <Button
            onClick={handleImportConfirm}
            variant="contained"
            disabled={importPreview.length === 0}
            sx={BTN_IMPORT}
          >
            Thêm {importPreview.length} thg gia
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Undo Snackbar ── */}
      <Snackbar
        open={undoOpen}
        autoHideDuration={5000}
        onClose={() => { setUndoOpen(false); setRemovedMerchant(null); }}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        sx={{ zIndex: 1500 }}
      >
        <Alert
          severity="warning"
          variant="filled"
          sx={ALERT_WARNING}
          action={
            <Button color="inherit" size="small" onClick={handleUndoRemoval}>
              Hoàn tác
            </Button>
          }
        >
          Đã xóa "{removedMerchant?.nickname}". Hoàn tác?
        </Alert>
      </Snackbar>

      {/* ── Export Toast ── */}
      <Snackbar
        open={toastOpen}
        autoHideDuration={2000}
        onClose={() => setToastOpen(false)}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        sx={{ zIndex: 1500 }}
      >
        <Alert
          severity="success"
          variant="filled"
          sx={ALERT_SUCCESS}
        >
          Đã sao chép danh sách!
        </Alert>
      </Snackbar>
    </div>
  );
}

export default LayoutPrice;
